import type { UseAuthReturn } from "zudoku/hooks";
import type { ZudokuContext } from "zudoku";

export type Locale = "en" | "es" | "pt";

export const LOCALES: readonly Locale[] = ["en", "es", "pt"] as const;

export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  es: "Español",
  pt: "Português",
};

export const LOCALE_PATH_PREFIX: Record<Locale, string> = {
  en: "",
  es: "/es",
  pt: "/pt",
};

export function getLocaleFromPath(pathname: string): Locale {
  if (pathname === "/es" || pathname.startsWith("/es/")) {
    return "es";
  }
  if (pathname === "/pt" || pathname.startsWith("/pt/")) {
    return "pt";
  }
  return "en";
}

export function getDocsPathPrefix(locale: Locale): string {
  switch (locale) {
    case "es":
      return "es/docs/";
    case "pt":
      return "pt/docs/";
    default:
      return "docs/";
  }
}

export function swapLocaleInPath(pathname: string, targetLocale: Locale): string {
  const currentLocale = getLocaleFromPath(pathname);
  const currentPrefix = LOCALE_PATH_PREFIX[currentLocale];
  const targetPrefix = LOCALE_PATH_PREFIX[targetLocale];

  if (currentPrefix && pathname.startsWith(currentPrefix)) {
    const remainder = pathname.slice(currentPrefix.length);
    return `${targetPrefix}${remainder}` || targetPrefix || "/";
  }

  if (pathname.startsWith("/docs")) {
    const remainder = pathname.slice("/docs".length);
    return `${targetPrefix}/docs${remainder}`;
  }

  if (targetLocale === "en") {
    return "/docs/introduction/overview";
  }

  return `${targetPrefix}/docs/introduction/overview`;
}

export function showForLocale(locale: Locale) {
  return (_params: { context: ZudokuContext; auth: UseAuthReturn }) => {
    if (typeof window === "undefined") {
      return locale === "en";
    }
    const pathname = window.location.pathname;
    if (locale === "es") {
      return pathname === "/es" || pathname.startsWith("/es/");
    }
    if (locale === "pt") {
      return pathname === "/pt" || pathname.startsWith("/pt/");
    }
    return (
      pathname !== "/es" &&
      !pathname.startsWith("/es/") &&
      pathname !== "/pt" &&
      !pathname.startsWith("/pt/")
    );
  };
}

export function showForEn(_params: { context: ZudokuContext; auth: UseAuthReturn }) {
  if (typeof window === "undefined") {
    return true;
  }
  const pathname = window.location.pathname;
  return (
    pathname !== "/es" &&
    !pathname.startsWith("/es/") &&
    pathname !== "/pt" &&
    !pathname.startsWith("/pt/")
  );
}

export function showForEs(_params: { context: ZudokuContext; auth: UseAuthReturn }) {
  if (typeof window === "undefined") {
    return false;
  }
  const pathname = window.location.pathname;
  return pathname === "/es" || pathname.startsWith("/es/");
}

export function showForPt(_params: { context: ZudokuContext; auth: UseAuthReturn }) {
  if (typeof window === "undefined") {
    return false;
  }
  const pathname = window.location.pathname;
  return pathname === "/pt" || pathname.startsWith("/pt/");
}
